import React, { useEffect, useMemo, useState } from "react";
import {
  createParaplanningCase,
  updateParaplanningCase,
} from "../services/paraplanningCasesService";
import { useParaplanningCharges } from "../hooks/useParaplanningCharges";
import {
  getParaplannerNameByEmail,
  getParaplannerTypeByEmail,
} from "../utils/paraplannerGroups";
import { formatCurrency } from "../utils/formatCurrency";
import {
  buildAdditionalChargeOptions,
  buildAdviserOptions,
  buildAssignedToOptions,
  buildCaseTypeOptions,
  createCustomChargeId,
  getInitialAddCaseFormData,
  getSelectedTaskLabels,
  getTodayDateString,
  moneyToNumber,
} from "../utils/addCaseModalUtils";
import { PARAPLANNING_TASK_OPTIONS } from "../constants/paraplanningTaskOptions";
import { PARAPLANNING_ASSIGNED_TO_OPTIONS } from "../constants/paraplanningAssignedToOptions";

import MultipleClientsSection from "../components/MultipleClientsSection";
import AdditionalChargesSection from "../components/AdditionalChargesSection";
import ParaplanningInputField from "../components/ParaplanningInputField";
import ParaplanningSelectField from "../components/ParaplanningSelectField";
import ParaplanningDateField from "../components/ParaplanningDateField";
import ParaplanningToggle from "../components/ParaplanningToggle";
import ParaplanningCheckboxSelect from "../components/ParaplanningCheckboxSelect";
import ParaplanningTextareaField from "../components/ParaplanningTextareaField";

import { useAdviserListWithUnappointed } from "../../../context/Adviser/Utils/UnappointedAppointedAdviserEmailList";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close.svg";
import PrimaryButton from "../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../DesignSystem/Buttons/SecondaryButton";
import Checkbox from "../../DesignSystem/Inputs/Checkbox";
import "../../../css/Praplanning/AddCaseModal.css";

const getCatalogIdFromUserCategory = (loggedInEmail) => {
  const normalisedEmail = String(loggedInEmail || "")
    .trim()
    .toLowerCase();

  const loggedInUser = PARAPLANNING_ASSIGNED_TO_OPTIONS.find(
    (user) =>
      String(user.email || "")
        .trim()
        .toLowerCase() === normalisedEmail,
  );

  const categories = (loggedInUser?.categories || []).map((category) =>
    String(category || "")
      .trim()
      .toUpperCase(),
  );

  if (
    categories.includes("MA") ||
    categories.includes("MP") ||
    categories.includes("M&P") ||
    categories.includes("MORTGAGE_PROTECTION")
  ) {
    return "mortgage_protection";
  }

  if (categories.includes("FP") || categories.includes("FINANCIAL_PLANNING")) {
    return "financial_planning";
  }

  return getParaplannerTypeByEmail(loggedInEmail);
};

const normaliseSavedModifiers = (modifiers = []) => {
  return modifiers.map((modifier) => {
    const isDiscount =
      modifier.isDiscount === true || modifier.type === "discount";

    const isCustom =
      modifier.isCustom === true || modifier.type === "custom" || isDiscount;

    return {
      id: modifier.id || `modifier_${Date.now()}`,
      originalModifierId:
        modifier.originalModifierId ||
        modifier.modifierId ||
        modifier.catalogModifierId ||
        "",
      label:
        modifier.label ||
        modifier.name ||
        (isDiscount ? "Discount" : "Custom Charge"),
      amount: moneyToNumber(modifier.amount),
      amountType: modifier.amountType || "fixed",
      quantity: Math.max(Number(modifier.quantity || 1), 1),
      type: isDiscount ? "discount" : isCustom ? "custom" : "charge",
      isDiscount,
      isCustom,
    };
  });
};

const mapCaseToFormData = (caseItem, fallbackData) => {
  if (!caseItem) return fallbackData;

  const savedClients =
    Array.isArray(caseItem.clients) && caseItem.clients.length
      ? caseItem.clients
      : caseItem.client
        ? [caseItem.client]
        : [];

  const isCompleted = caseItem.status === "completed";

  const savedBaseCost =
    caseItem.costs?.baseCost !== undefined && caseItem.costs?.baseCost !== null
      ? Number(caseItem.costs.baseCost)
      : null;

  return {
    ...fallbackData,

    catalogId: caseItem.catalogId || fallbackData.catalogId,
    categoryId: caseItem.categoryId || "",
    itemId: caseItem.itemId || "",

    startDate: caseItem.startDate || fallbackData.startDate,
    completionDate:
      caseItem.completionDate ||
      (isCompleted ? caseItem.startDate : "") ||
      fallbackData.completionDate,

    adviserName: caseItem.adviser?.name || "",
    adviserEmail: caseItem.adviser?.email || "",

    clientFirstName: "",
    clientLastName: "",

    clients: savedClients.map((client, index) => ({
      id: client.id || `client_${index}`,
      firstName: client.firstName || "",
      lastName: client.lastName || "",
      name:
        client.name ||
        `${client.firstName || ""} ${client.lastName || ""}`.trim(),
      isPrimary: index === 0 || client.isPrimary === true,
    })),

    assignedToName: caseItem.assignedTo?.name || fallbackData.assignedToName,
    assignedToEmail: caseItem.assignedTo?.email || fallbackData.assignedToEmail,
    highPriority: Boolean(caseItem.highPriority),

    status: caseItem.status || "in_progress",

    // Important:
    // For in-progress cases, leave this empty so the selected case type's
    // default charge is used when the item loads.
    baseChargeOverride:
      isCompleted && savedBaseCost !== null ? String(savedBaseCost) : "",

    additionalChargeLabel: "",
    additionalChargeAmount: "",
    additionalChargeCatalogId: "",
    additionalChargeType: "",
    additionalChargeIsDiscount: false,
    additionalChargeQuantity: 1,

    selectedModifiers: normaliseSavedModifiers(
      caseItem.chargeSnapshot?.modifiers || [],
    ),

    tasksForCase: caseItem.taskIds || [],
    notes: caseItem.notes || "",
  };
};

const AddCaseModal = ({
  defaultCatalogId,
  loggedInEmail,
  initialCase = null,
  mode = "create",
  onClose,
  onCreated,
}) => {
  const isEditMode = mode === "edit" && initialCase?.id;

  const { advisers, loadingAdvisers } = useAdviserListWithUnappointed();

  const loggedInParaplannerName = getParaplannerNameByEmail(loggedInEmail);

  const defaultType =
    defaultCatalogId || getCatalogIdFromUserCategory(loggedInEmail);

  const assignedToOptions = useMemo(() => {
    return buildAssignedToOptions(PARAPLANNING_ASSIGNED_TO_OPTIONS);
  }, []);

  const defaultAssignedTo = useMemo(() => {
    const loggedInAssignedUser = assignedToOptions.find(
      (person) => person.email?.toLowerCase() === loggedInEmail?.toLowerCase(),
    );

    return loggedInAssignedUser || null;
  }, [assignedToOptions, loggedInEmail]);

  useEffect(() => {
    const normalisedEmail = String(loggedInEmail || "")
      .trim()
      .toLowerCase();

    const matchedAssignedOption = PARAPLANNING_ASSIGNED_TO_OPTIONS.find(
      (user) =>
        String(user.email || "")
          .trim()
          .toLowerCase() === normalisedEmail,
    );

    const fallbackCatalogFromEmail = getParaplannerTypeByEmail(loggedInEmail);

    // console.info("[AddCaseModal] Default user mapping", {
    //   loggedInEmail,
    //   mode,
    //   isEditMode,
    //   matchedAssignedOption,
    //   categoryBasedCatalog: getCatalogIdFromUserCategory(loggedInEmail),
    //   fallbackCatalogFromEmail,
    //   defaultCatalogIdProp: defaultCatalogId || null,
    //   selectedDefaultCatalog: defaultType,
    //   selectedDefaultAssignedTo: defaultAssignedTo
    //     ? {
    //         email: defaultAssignedTo.email,
    //         name: defaultAssignedTo.name,
    //       }
    //     : null,
    //   initialCaseId: initialCase?.id || null,
    // });
  }, [
    loggedInEmail,
    mode,
    isEditMode,
    defaultCatalogId,
    defaultType,
    defaultAssignedTo,
    initialCase,
  ]);

  const fallbackFormData = useMemo(() => {
    return getInitialAddCaseFormData({
      defaultType,
      loggedInParaplannerName: defaultAssignedTo?.name || "",
      loggedInEmail: defaultAssignedTo?.email || "",
    });
  }, [defaultType, defaultAssignedTo]);

  const [formData, setFormData] = useState(() =>
    mapCaseToFormData(initialCase, fallbackFormData),
  );

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const { catalogs, loadingCharges, chargesError } = useParaplanningCharges(
    formData.catalogId,
  );

  const adviserOptions = useMemo(() => {
    return buildAdviserOptions(advisers);
  }, [advisers]);

  useEffect(() => {
    if (isEditMode) return;

    setFormData((prev) => ({
      ...prev,
      catalogId: prev.catalogId || defaultType,
      assignedToName: defaultAssignedTo?.name || "",
      assignedToEmail: defaultAssignedTo?.email || "",
      highPriority: Boolean(prev.highPriority),
    }));
  }, [isEditMode, defaultType, defaultAssignedTo]);

  useEffect(() => {
    if (!isEditMode) return;

    setFormData(mapCaseToFormData(initialCase, fallbackFormData));
  }, [isEditMode, initialCase, fallbackFormData]);

  const selectedCatalog = useMemo(() => {
    return catalogs?.[0] || null;
  }, [catalogs]);

  const caseTypeOptions = useMemo(() => {
    return buildCaseTypeOptions(selectedCatalog);
  }, [selectedCatalog]);

  const selectedCaseTypeValue =
    formData.categoryId && formData.itemId
      ? `${formData.categoryId}__${formData.itemId}`
      : "";

  const selectedCaseType = useMemo(() => {
    return caseTypeOptions.find(
      (option) =>
        option.categoryId === formData.categoryId &&
        option.itemId === formData.itemId,
    );
  }, [caseTypeOptions, formData.categoryId, formData.itemId]);

  const selectedItem = selectedCaseType?.item || null;

  const additionalChargeOptions = useMemo(() => {
    return buildAdditionalChargeOptions(selectedItem);
  }, [selectedItem]);

  const originalBaseCharge = moneyToNumber(selectedItem?.amount);

  const baseCharge =
    formData.baseChargeOverride === ""
      ? originalBaseCharge
      : moneyToNumber(formData.baseChargeOverride);

  const selectedModifierTotal = useMemo(() => {
    return formData.selectedModifiers.reduce((sum, modifier) => {
      const amount =
        moneyToNumber(modifier.amount) * Number(modifier.quantity || 1);

      return modifier.isDiscount ? sum - amount : sum + amount;
    }, 0);
  }, [formData.selectedModifiers]);

  const totalCharge = baseCharge + selectedModifierTotal;

  const resetAdditionalChargeFields = {
    additionalChargeLabel: "",
    additionalChargeAmount: "",
    additionalChargeCatalogId: "",
    additionalChargeType: "",
    additionalChargeIsDiscount: false,
    additionalChargeQuantity: 1,
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const setCatalogId = (catalogId) => {
    setFormData((prev) => ({
      ...prev,
      catalogId,
      categoryId: "",
      itemId: "",
      selectedModifiers: [],
      baseChargeOverride: "",
      ...resetAdditionalChargeFields,
    }));
  };

  const handleCaseTypeChange = (value) => {
    const selected = caseTypeOptions.find(
      (option) => option.value === value || option.id === value,
    );

    if (!selected) {
      // console.warn("[AddCaseModal] No selected case type matched value:", {
      //   value,
      //   availableValues: caseTypeOptions.map((option) => option.value),
      // });

      return;
    }

    setFormData((prev) => ({
      ...prev,
      categoryId: selected.categoryId || "",
      itemId: selected.itemId || "",
      selectedModifiers: [],
      baseChargeOverride: "",
      ...resetAdditionalChargeFields,
    }));
  };

  const handleAdditionalChargeSelect = (optionId) => {
    const selectedOption = additionalChargeOptions.find(
      (option) => option.id === optionId || option.value === optionId,
    );

    if (!selectedOption) return;

    if (selectedOption.id === "custom_charge") {
      setFormData((prev) => ({
        ...prev,
        additionalChargeCatalogId: "custom_charge",
        additionalChargeLabel: "Custom Charge",
        additionalChargeAmount: "",
        additionalChargeType: "custom",
        additionalChargeIsDiscount: false,
        additionalChargeQuantity: 1,
      }));

      return;
    }

    if (selectedOption.id === "discount") {
      setFormData((prev) => ({
        ...prev,
        additionalChargeCatalogId: "discount",
        additionalChargeLabel: "Discount",
        additionalChargeAmount: "",
        additionalChargeType: "discount",
        additionalChargeIsDiscount: true,
        additionalChargeQuantity: 1,
      }));

      return;
    }

    const modifier = selectedItem?.modifiers?.find(
      (item) => item.id === selectedOption.id,
    );

    if (!modifier) return;

    setFormData((prev) => ({
      ...prev,
      additionalChargeCatalogId: modifier.id,
      additionalChargeLabel:
        modifier.label || modifier.name || selectedOption.label || "",
      additionalChargeAmount: String(modifier.amount || ""),
      additionalChargeType: "charge",
      additionalChargeIsDiscount: false,
      additionalChargeQuantity: 1,
    }));
  };

  const addAdditionalCharge = () => {
    const selectedType = formData.additionalChargeType || "charge";
    const isDiscount = Boolean(formData.additionalChargeIsDiscount);

    const selectedOption = additionalChargeOptions.find(
      (option) =>
        option.id === formData.additionalChargeCatalogId ||
        option.value === formData.additionalChargeCatalogId,
    );

    const label =
      formData.additionalChargeLabel ||
      selectedOption?.label ||
      selectedOption?.name ||
      (isDiscount ? "Discount" : "Custom Charge");

    const amount = moneyToNumber(formData.additionalChargeAmount);
    const quantity = Math.max(
      Number(formData.additionalChargeQuantity || 1),
      1,
    );

    if (!formData.additionalChargeCatalogId) {
      setError("Please select an additional charge type.");
      return;
    }

    if (!amount) {
      setError("Please enter an amount.");
      return;
    }

    const isCatalogueCharge =
      formData.additionalChargeCatalogId &&
      formData.additionalChargeCatalogId !== "custom_charge" &&
      formData.additionalChargeCatalogId !== "discount";

    const id = isCatalogueCharge
      ? `${formData.additionalChargeCatalogId}_${Date.now()}`
      : createCustomChargeId(label);

    setError("");

    setFormData((prev) => ({
      ...prev,
      selectedModifiers: [
        ...prev.selectedModifiers,
        {
          id,
          originalModifierId: isCatalogueCharge
            ? prev.additionalChargeCatalogId
            : "",
          label,
          amount,
          amountType: "fixed",
          quantity,
          type: selectedType,
          isDiscount,
          isCustom: !isCatalogueCharge,
        },
      ],
      ...resetAdditionalChargeFields,
    }));
  };

  const updateSelectedModifier = (modifierId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      selectedModifiers: prev.selectedModifiers.map((modifier) => {
        if (modifier.id !== modifierId) return modifier;

        if (field === "amount") {
          return {
            ...modifier,
            amount: moneyToNumber(value),
          };
        }

        if (field === "quantity") {
          return {
            ...modifier,
            quantity: Math.max(Number(value || 1), 1),
          };
        }

        return {
          ...modifier,
          [field]: value,
        };
      }),
    }));
  };

  const updateSelectedModifierChargeType = (modifierId, optionId) => {
    const selectedOption = additionalChargeOptions.find(
      (option) => option.id === optionId || option.value === optionId,
    );

    if (!selectedOption) return;

    setFormData((prev) => ({
      ...prev,
      selectedModifiers: prev.selectedModifiers.map((modifier) => {
        if (modifier.id !== modifierId) return modifier;

        if (selectedOption.id === "custom_charge") {
          return {
            ...modifier,
            originalModifierId: "",
            label: "Custom Charge",
            type: "custom",
            isDiscount: false,
            isCustom: true,
          };
        }

        if (selectedOption.id === "discount") {
          return {
            ...modifier,
            originalModifierId: "",
            label: "Discount",
            type: "discount",
            isDiscount: true,
            isCustom: true,
          };
        }

        const catalogueModifier = selectedItem?.modifiers?.find(
          (item) => item.id === selectedOption.id,
        );

        return {
          ...modifier,
          originalModifierId: selectedOption.id,
          label:
            catalogueModifier?.label ||
            catalogueModifier?.name ||
            selectedOption.label ||
            selectedOption.name ||
            "",
          amount: moneyToNumber(catalogueModifier?.amount ?? modifier.amount),
          type: "charge",
          isDiscount: false,
          isCustom: false,
        };
      }),
    }));
  };

  const getSelectedModifierDropdownValue = (modifier) => {
    if (modifier.originalModifierId) return modifier.originalModifierId;

    if (modifier.isDiscount) return "discount";

    if (modifier.type === "custom") return "custom_charge";

    const matchingOption = additionalChargeOptions.find((option) => {
      return (
        option.id === modifier.id ||
        option.value === modifier.id ||
        String(modifier.id || "").startsWith(`${option.id}_`)
      );
    });

    if (matchingOption) return matchingOption.id || matchingOption.value;

    return "custom_charge";
  };
  const removeAdditionalCharge = (modifierId) => {
    setFormData((prev) => ({
      ...prev,
      selectedModifiers: prev.selectedModifiers.filter(
        (item) => item.id !== modifierId,
      ),
    }));
  };

  const toggleCaseCompleted = (checked) => {
    setFormData((prev) => ({
      ...prev,
      status: checked ? "completed" : "in_progress",
      completionDate: checked
        ? prev.completionDate || prev.startDate || getTodayDateString()
        : "",
      selectedModifiers: checked ? prev.selectedModifiers : [],
      tasksForCase: checked ? prev.tasksForCase : [],
      additionalChargeLabel: checked ? prev.additionalChargeLabel : "",
      additionalChargeAmount: checked ? prev.additionalChargeAmount : "",
      additionalChargeCatalogId: checked ? prev.additionalChargeCatalogId : "",
      additionalChargeType: checked ? prev.additionalChargeType : "",
      additionalChargeIsDiscount: checked
        ? prev.additionalChargeIsDiscount
        : false,
      additionalChargeQuantity: checked ? prev.additionalChargeQuantity : 1,
    }));
  };

  const addClient = () => {
    const firstName = String(formData.clientFirstName || "").trim();
    const lastName = String(formData.clientLastName || "").trim();

    if (!firstName && !lastName) {
      setError("Please enter a client name before adding.");
      return;
    }

    setError("");

    setFormData((prev) => ({
      ...prev,
      clients: [
        ...(prev.clients || []),
        {
          id: `client_${Date.now()}`,
          firstName,
          lastName,
          name: `${firstName} ${lastName}`.trim(),
        },
      ],
      clientFirstName: "",
      clientLastName: "",
    }));
  };

  const updateClient = (clientId, field, value) => {
    setFormData((prev) => ({
      ...prev,
      clients: (prev.clients || []).map((client) => {
        if (client.id !== clientId) return client;

        const updatedClient = {
          ...client,
          [field]: value,
        };

        return {
          ...updatedClient,
          name: `${updatedClient.firstName || ""} ${
            updatedClient.lastName || ""
          }`.trim(),
        };
      }),
    }));
  };

  const removeClient = (clientId) => {
    setFormData((prev) => ({
      ...prev,
      clients: (prev.clients || []).filter((client) => client.id !== clientId),
    }));
  };

  const buildClientsForSubmit = () => {
    const addedClients = (formData.clients || [])
      .map((client) => {
        const firstName = String(client.firstName || "").trim();
        const lastName = String(client.lastName || "").trim();

        return {
          id: client.id,
          firstName,
          lastName,
          name: `${firstName} ${lastName}`.trim(),
        };
      })
      .filter((client) => client.firstName || client.lastName);

    const pendingFirstName = String(formData.clientFirstName || "").trim();
    const pendingLastName = String(formData.clientLastName || "").trim();

    const pendingClient =
      pendingFirstName || pendingLastName
        ? [
            {
              id: `client_pending_${Date.now()}`,
              firstName: pendingFirstName,
              lastName: pendingLastName,
              name: `${pendingFirstName} ${pendingLastName}`.trim(),
            },
          ]
        : [];

    return [...addedClients, ...pendingClient].map((client, index) => ({
      ...client,
      isPrimary: index === 0,
    }));
  };

  const buildCasePayload = () => {
    const selectedTaskLabels = getSelectedTaskLabels(
      formData.tasksForCase,
      PARAPLANNING_TASK_OPTIONS,
    );

    const clients = buildClientsForSubmit();

    const selectedCategory = selectedCaseType?.category || null;
    const selectedChargeItem = selectedCaseType?.item || null;

    const isCompleted = formData.status === "completed";

    const modifiers = isCompleted ? formData.selectedModifiers : [];

    const basePayload = {
      catalogId: formData.catalogId,
      categoryId: formData.categoryId,
      itemId: formData.itemId,

      startDate: formData.startDate,

      completionDate: isCompleted
        ? formData.completionDate || formData.startDate || getTodayDateString()
        : "",

      adviser: {
        name: formData.adviserName,
        email: formData.adviserEmail,
      },

      client: clients[0],
      clients,

      type: selectedChargeItem?.name || initialCase?.type || "",

      chargeSnapshot: {
        versionId: selectedCatalog?.currentVersionId || "",
        catalogName: selectedCatalog?.name || "",
        categoryName: selectedCategory?.name || "",
        itemName: selectedChargeItem?.name || "",
        itemDescription: selectedChargeItem?.description || "",
        baseAmount: baseCharge,
        currency: selectedChargeItem?.currency || "GBP",
        amountType: selectedChargeItem?.amountType || "fixed",
        modifiers,
      },

      costs: {
        baseCost: isCompleted ? baseCharge : 0,
        additionalCost: isCompleted ? selectedModifierTotal : 0,
        totalCost: isCompleted ? totalCharge : 0,
      },

      assignedTo: {
        name: formData.assignedToName,
        email: formData.assignedToEmail,
      },

      highPriority: Boolean(formData.highPriority),

      status: formData.status,

      notes: formData.notes || "",

      tasksForCase: isCompleted ? selectedTaskLabels : [],

      taskIds: isCompleted ? formData.tasksForCase : [],

      createdBy: isEditMode
        ? initialCase?.createdBy || {
            name: loggedInParaplannerName,
            email: loggedInEmail,
          }
        : {
            name: loggedInParaplannerName,
            email: loggedInEmail,
          },
    };

    if (isEditMode) {
      return {
        ...basePayload,
        lastEditedBy: {
          name: loggedInParaplannerName,
          email: loggedInEmail,
        },
      };
    }

    return basePayload;
  };
  const submitCase = async (event) => {
    event.preventDefault();

    // if (!formData.catalogId || !formData.categoryId || !formData.itemId) {
    //   setError("Please select a case type.");
    //   return;
    // }

    const clients = buildClientsForSubmit();

    if (!clients.length) {
      setError("Please add at least one client.");
      return;
    }

    if (!clients[0]?.firstName || !clients[0]?.lastName) {
      setError("Please enter the main client's first and last name.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      const payload = buildCasePayload();

      if (isEditMode) {
        await updateParaplanningCase(initialCase.id, payload);
      } else {
        await createParaplanningCase(payload);
      }

      onCreated();
    } catch (error) {
      setError(error.message || "Could not save case.");
    } finally {
      setSaving(false);
    }
  };

  const validAssignedToEmail = assignedToOptions.some(
    (option) => option.id === formData.assignedToEmail,
  )
    ? formData.assignedToEmail
    : "";

  const createButtonLabel = validAssignedToEmail
    ? "Add Case"
    : "Add to Pipeline";

  return (
    <div className="ParaplanningModalOverlay">
      <form className="ParaplanningModal" onSubmit={submitCase}>
        <div className="ParaplanningModal__Header">
          <div className="FP_Subtitle_Semibold primary">
            {isEditMode ? "Edit Case" : "Add New Case"}
          </div>

          <button
            type="button"
            className="ParaplanningModal__CloseButton"
            onClick={onClose}
          >
            <CloseIcon />
          </button>
        </div>

        <div className="ParaplanningCatalogButtons">
          <button
            type="button"
            onClick={() => setCatalogId("financial_planning")}
            className={
              formData.catalogId === "financial_planning"
                ? "ParaplanningCatalogButton ParaplanningCatalogButton--Active"
                : "ParaplanningCatalogButton ParaplanningCatalogButton--Inactive"
            }
          >
            Financial Planning
          </button>

          <button
            type="button"
            onClick={() => setCatalogId("mortgage_protection")}
            className={
              formData.catalogId === "mortgage_protection"
                ? "ParaplanningCatalogButton ParaplanningCatalogButton--Active"
                : "ParaplanningCatalogButton ParaplanningCatalogButton--Inactive"
            }
          >
            Mortgage & Protection
          </button>
        </div>

        {error && <div className="ParaplanningDashboard__Error">{error}</div>}

        {chargesError && (
          <div className="ParaplanningDashboard__Error">{chargesError}</div>
        )}

        {loadingCharges ? (
          <p>
            Loading{" "}
            {formData.catalogId === "financial_planning"
              ? "Financial Planning"
              : "Mortgage & Protection"}{" "}
            charges...
          </p>
        ) : (
          <>
            <div className="ParaplanningModal__Grid">
              <ParaplanningDateField
                label="Start Date"
                required
                value={formData.startDate}
                onChange={(value) => updateField("startDate", value)}
              />

              <ParaplanningSelectField
                label="Adviser Name"
                required
                value={formData.adviserEmail}
                placeholder={
                  loadingAdvisers ? "Loading advisers..." : "Select Adviser"
                }
                disabled={loadingAdvisers}
                options={adviserOptions}
                onChange={(value) => {
                  const selectedAdviser = adviserOptions.find(
                    (option) => option.id === value,
                  );

                  updateField("adviserEmail", value);
                  updateField("adviserName", selectedAdviser?.name || "");
                }}
              />

              <MultipleClientsSection
                clientFirstName={formData.clientFirstName}
                clientLastName={formData.clientLastName}
                clients={formData.clients}
                onFieldChange={updateField}
                onAddClient={addClient}
                onUpdateClient={updateClient}
                onRemoveClient={removeClient}
              />

              <div className="ParaplanningField ParaplanningField--Full">
                <ParaplanningSelectField
                  label="Case Type"
                  // required
                  value={selectedCaseTypeValue}
                  placeholder="Select case type"
                  options={caseTypeOptions}
                  onChange={handleCaseTypeChange}
                />
              </div>

              <ParaplanningSelectField
                label="Assigned To"
                // required
                value={validAssignedToEmail}
                placeholder="Select paraplanner"
                options={assignedToOptions}
                onChange={(value) => {
                  const selectedUser = assignedToOptions.find(
                    (option) => option.id === value,
                  );

                  updateField("assignedToEmail", value || "");
                  updateField("assignedToName", selectedUser?.name || "");
                }}
              />
            <Checkbox
              id="high-priority"
              name="high-priority"
              label="Mark as High Priority"
              checked={Boolean(formData.highPriority)}
              onChange={(event) =>
                updateField("highPriority", event.target.checked)
              }
            />
            </div>
            <ParaplanningTextareaField
              label="Notes"
              value={formData.notes}
              placeholder="Enter any further notes here"
              className="ParaplanningCompletedSection__Textarea"
              onChange={(value) => updateField("notes", value)}
            />

            <ParaplanningToggle
              label="Status"
              checked={formData.status === "completed"}
              onChange={toggleCaseCompleted}
            />

            {formData.status === "completed" && (
              <div className="ParaplanningCompletedSection">
                <ParaplanningDateField
                  label="Completion Date"
                  required
                  value={formData.completionDate}
                  onChange={(value) => updateField("completionDate", value)}
                />

                <ParaplanningInputField
                  label="Charges"
                  value={
                    formData.baseChargeOverride === ""
                      ? originalBaseCharge
                        ? String(originalBaseCharge)
                        : ""
                      : formData.baseChargeOverride
                  }
                  placeholder="£0.00"
                  onChange={(value) => updateField("baseChargeOverride", value)}
                />

                <AdditionalChargesSection
                  formData={formData}
                  additionalChargeOptions={additionalChargeOptions}
                  onAddCharge={addAdditionalCharge}
                  onChargeSelect={handleAdditionalChargeSelect}
                  onFieldChange={updateField}
                  selectedModifiers={formData.selectedModifiers}
                  getSelectedModifierDropdownValue={
                    getSelectedModifierDropdownValue
                  }
                  onModifierTypeChange={updateSelectedModifierChargeType}
                  onModifierFieldChange={updateSelectedModifier}
                  onRemoveModifier={removeAdditionalCharge}
                />

                <ParaplanningCheckboxSelect
                  label="Tasks For Case"
                  value={formData.tasksForCase}
                  options={PARAPLANNING_TASK_OPTIONS}
                  placeholder="Select completed tasks"
                  helperText="Select all tasks relevant for this case"
                  onChange={(selectedTasks) =>
                    updateField("tasksForCase", selectedTasks)
                  }
                />

                <div className="ParaplanningModal__Total">
                  <span>Total Cost</span>
                  <strong>{formatCurrency(totalCharge)}</strong>
                </div>
              </div>
            )}

            <div className="ParaplanningModal__Actions">
              <SecondaryButton type="button" label="Cancel" onClick={onClose} />

              <PrimaryButton
                type="submit"
                label={
                  saving
                    ? "Saving..."
                    : isEditMode
                      ? "Save Changes"
                      : createButtonLabel
                }
                disabled={saving}
              />
            </div>
          </>
        )}
      </form>
    </div>
  );
};

export default AddCaseModal;
